import axios from 'axios';
import { messageLines, objectKeyPaths, redactUrl, truncate } from './redact';
import {
  ATTACHED_ERROR_KEY, ErrorKind, ErrorSource, ErrorTier, ExpectedError,
  type NormalizeContext, type NormalizedError, type NormalizedRequest
} from './errorTypes';

/**
 * The universal seam: every error path in the app converges here.
 *
 * It is the only place that sees all of them — HTTP errors from `apiClient`, the four bare-`axios`
 * calls that bypass it, Vue render errors, chunk-load failures, plain `TypeError`s, and the bare
 * strings a couple of stores pass to `setError`. Being pure, it is also the only part that is
 * trivially testable, which matters because classification is where the subtle bugs live.
 */

/**
 * Error `name`s that mean "show the user, don't tell Sentry".
 *
 * Several call sites fabricate `{ name, message }` literals purely to reuse `setError` as a toast.
 * This list suppresses them without editing those sites; `ExpectedError` is the honest fix and lets
 * the list shrink. `NotInitialzed` is spelled as it is in the source — matching it verbatim is the
 * whole point.
 */
export const EXPECTED_ERROR_NAMES: readonly string[] = [
  'ExpectedError', 'Locked', 'NotAllowedException', 'ParseException', 'NotInitialzed'
];

const COPY: Record<ErrorKind, { titleKey: string; messageKey: string }> = {
  [ErrorKind.NETWORK]: { titleKey: 'error_network_title', messageKey: 'error_network_message' },
  [ErrorKind.TIMEOUT]: { titleKey: 'error_timeout_title', messageKey: 'error_timeout_message' },
  [ErrorKind.AUTH_EXPIRED]: { titleKey: 'error_session_title', messageKey: 'error_session_message' },
  [ErrorKind.FORBIDDEN]: { titleKey: 'error_forbidden_title', messageKey: 'error_forbidden_message' },
  [ErrorKind.NOT_FOUND]: { titleKey: 'error_not_found_title', messageKey: 'error_not_found_message' },
  [ErrorKind.VALIDATION]: { titleKey: 'error_validation_title', messageKey: 'error_validation_message' },
  [ErrorKind.CONFLICT]: { titleKey: 'error_conflict_title', messageKey: 'error_conflict_message' },
  [ErrorKind.RATE_LIMITED]: { titleKey: 'error_rate_limited_title', messageKey: 'error_rate_limited_message' },
  [ErrorKind.SERVER]: { titleKey: 'error_server_title', messageKey: 'error_server_message' },
  [ErrorKind.CHUNK_LOAD]: { titleKey: 'error_new_version_title', messageKey: 'error_new_version_message' },
  [ErrorKind.RENDER]: { titleKey: 'error_render_title', messageKey: 'error_render_message' },
  [ErrorKind.ROUTE]: { titleKey: 'error_route_title', messageKey: 'error_route_message' },
  [ErrorKind.UNHANDLED_REJECTION]: { titleKey: 'error_generic_title', messageKey: 'error_generic_message' },
  [ErrorKind.EXPECTED]: { titleKey: 'error_generic_title', messageKey: 'error_generic_message' },
  [ErrorKind.UNKNOWN]: { titleKey: 'error_generic_title', messageKey: 'error_generic_message' }
};

/** Statuses that mean "the request was wrong", not "the app is broken". Shown, never reported. */
const NON_REPORTABLE_STATUSES: readonly number[] = [400, 401, 403, 404, 409, 422, 429];

const KIND_BY_STATUS: Record<number, ErrorKind> = {
  400: ErrorKind.VALIDATION,
  401: ErrorKind.FORBIDDEN,
  403: ErrorKind.FORBIDDEN,
  404: ErrorKind.NOT_FOUND,
  409: ErrorKind.CONFLICT,
  422: ErrorKind.VALIDATION,
  429: ErrorKind.RATE_LIMITED
};

const TIER_BY_KIND: Record<ErrorKind, ErrorTier> = {
  [ErrorKind.NETWORK]: ErrorTier.TOAST,
  [ErrorKind.TIMEOUT]: ErrorTier.TOAST,
  [ErrorKind.AUTH_EXPIRED]: ErrorTier.SILENT,
  [ErrorKind.FORBIDDEN]: ErrorTier.TOAST,
  [ErrorKind.NOT_FOUND]: ErrorTier.TOAST,
  [ErrorKind.VALIDATION]: ErrorTier.TOAST,
  [ErrorKind.CONFLICT]: ErrorTier.TOAST,
  [ErrorKind.RATE_LIMITED]: ErrorTier.TOAST,
  [ErrorKind.SERVER]: ErrorTier.TOAST,
  [ErrorKind.CHUNK_LOAD]: ErrorTier.FATAL,
  [ErrorKind.RENDER]: ErrorTier.FATAL,
  [ErrorKind.ROUTE]: ErrorTier.FATAL,
  [ErrorKind.UNHANDLED_REJECTION]: ErrorTier.TOAST,
  [ErrorKind.EXPECTED]: ErrorTier.TOAST,
  [ErrorKind.UNKNOWN]: ErrorTier.TOAST
};

// Deliberately excludes O, 0, I and 1 — this gets read aloud to support.
const REFERENCE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** A short, human-speakable id. Independent of Sentry, so it survives an unset DSN or a dropped event. */
export const newErrorReference = (): string => {
  const size = 8;
  const bytes = new Uint8Array(size);

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < size; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  const chars = Array.from(bytes, byte => REFERENCE_ALPHABET[byte % REFERENCE_ALPHABET.length]);
  return `${chars.slice(0, 4).join('')}-${chars.slice(4).join('')}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const MAX_MESSAGE_LINE = 300;

/**
 * A message body is only usable as copy if it is a string and not an HTML error page.
 *
 * A message of several lines is a list, such as every error in a data dictionary import-diff
 * refused, so each line is cut to length rather than the whole: cut whole, the list kept its first
 * two or three errors and dropped the rest. Every line is kept, since the import screen promises
 * every error.
 */
const usableBodyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || value.trim() === '' || /^\s*</.test(value)) {
    return undefined;
  }
  const lines = messageLines(value);
  return lines.length < 2 ?
    truncate(value, MAX_MESSAGE_LINE) :
    lines.map(line => truncate(line, MAX_MESSAGE_LINE)).join('\n');
};

/**
 * Pull user-facing copy out of a response body without trusting its shape.
 *
 * The old `setError` read `error.response?.data.error` and `.data.message.includes(...)` unguarded, so
 * it threw a `TypeError` on an HTML 502, an empty body, or a `message` that was not a string — the
 * handler crashed on exactly the failures most worth reporting.
 */
const readServerMessage = (data: unknown): string | undefined => {
  const asString = usableBodyString(data);
  if (asString) {
    return asString;
  }
  if (!isRecord(data)) {
    return undefined;
  }
  return usableBodyString(data.message) ?? usableBodyString(data.error) ?? usableBodyString(data.detail);
};

const readErrorCode = (data: unknown): string | undefined => {
  if (!isRecord(data)) {
    return undefined;
  }
  const candidate = data.error_code ?? data.errorCode ?? data.code;
  return typeof candidate === 'string' && /^[A-Z0-9_]{1,64}$/.test(candidate) ? candidate : undefined;
};

const messageOf = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (isRecord(error) && typeof error.message === 'string') {
    return error.message;
  }
  return '';
};

const nameOf = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.name;
  }
  return isRecord(error) && typeof error.name === 'string' ? error.name : undefined;
};

/**
 * A stale deploy, not a bug: the browser is holding an index that points at chunks no longer on the
 * CDN. Never reported — the fix is a reload, not a ticket.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  const message = messageOf(error);
  return /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    nameOf(error) === 'ChunkLoadError';
};

/** A 401 whose body says the session lapsed, as opposed to a 401 meaning "not your record". */
export const isAuthExpiredError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 401) {
    return false;
  }
  const data = error.response?.data;
  const candidates = [
    isRecord(data) ? data.message : undefined,
    isRecord(data) ? data.error : undefined,
    typeof data === 'string' ? data : undefined
  ];
  return candidates.some(value => typeof value === 'string' && /expired/i.test(value));
};

/** Vue-router cancels navigation by rejecting; that is flow control, not a failure. */
const isNavigationFailure = (error: unknown): boolean =>
  isRecord(error) && typeof error.type === 'number' && 'to' in error && 'from' in error;

/** Carry the classification on the thrown value so the interceptor and the catch block agree. */
export const attachNormalized = (error: object, normalized: NormalizedError): void => {
  try {
    Object.defineProperty(error, ATTACHED_ERROR_KEY, {
      value: normalized,
      enumerable: false,
      configurable: true,
      writable: true
    });
  } catch {
    // Frozen error objects are rare and not worth failing over — it just gets classified twice.
  }
};

export const getAttachedNormalized = (error: unknown): NormalizedError | undefined => {
  if (error == null || typeof error !== 'object') {
    return undefined;
  }
  const attached = (error as Record<string, unknown>)[ATTACHED_ERROR_KEY];
  return isRecord(attached) && typeof attached.reference === 'string' ? attached as unknown as NormalizedError : undefined;
};

const buildRequest = (error: unknown): NormalizedRequest | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const url = redactUrl(error.config?.url);
  const responseData = error.response?.data;
  const validationSource = isRecord(responseData) ? responseData.errors ?? responseData.fields : undefined;

  return {
    method: error.config?.method?.toUpperCase(),
    path: url?.path,
    params: url?.params,
    // Names only, never values — enough to see a payload was missing a field.
    bodyKeys: error.config?.data ? objectKeyPaths(safeParse(error.config.data)) : undefined,
    validationFields: validationSource ? objectKeyPaths(validationSource, 1) : undefined
  };
};

const safeParse = (body: unknown): unknown => {
  if (typeof body !== 'string') {
    return body;
  }
  try {
    return JSON.parse(body);
  } catch {
    return undefined;
  }
};

/** Collapse the variable parts of a message so repeats of one failure share a fingerprint. */
const fingerprintOf = (parts: (string | number | undefined)[], message: string): string => {
  const stable = message
    .replace(/[0-9a-f]{8,}/gi, '*')
    .replace(/\d+/g, '#')
    .slice(0, 120);
  return [...parts.filter(part => part != null), stable].join('|');
};

const classify = (error: unknown, context: NormalizeContext): { kind: ErrorKind; status?: number } => {
  if (context.kind) {
    return { kind: context.kind, status: axios.isAxiosError(error) ? error.response?.status : undefined };
  }
  if (isChunkLoadError(error)) {
    return { kind: ErrorKind.CHUNK_LOAD };
  }
  if (error instanceof ExpectedError || EXPECTED_ERROR_NAMES.includes(nameOf(error) ?? '')) {
    return { kind: ErrorKind.EXPECTED };
  }
  if (typeof error === 'string') {
    return { kind: ErrorKind.EXPECTED };
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (isAuthExpiredError(error)) {
      return { kind: ErrorKind.AUTH_EXPIRED, status };
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return { kind: ErrorKind.TIMEOUT, status };
    }
    if (status == null) {
      return { kind: ErrorKind.NETWORK };
    }
    if (KIND_BY_STATUS[status]) {
      return { kind: KIND_BY_STATUS[status], status };
    }
    return { kind: status >= 500 ? ErrorKind.SERVER : ErrorKind.UNKNOWN, status };
  }

  if (context.source === ErrorSource.RENDER) {
    return { kind: ErrorKind.RENDER };
  }
  if (context.source === ErrorSource.ROUTER) {
    return { kind: ErrorKind.ROUTE };
  }
  // A *fallback* rather than an explicit `context.kind`: a rejected axios promise nobody caught still
  // has to classify as NETWORK/AUTH_EXPIRED/SERVER above. Only a genuinely unclassifiable rejection
  // lands here, which is the one case the dedicated kind describes.
  if (context.source === ErrorSource.REJECTION) {
    return { kind: ErrorKind.UNHANDLED_REJECTION };
  }
  return { kind: ErrorKind.UNKNOWN };
};

export const normalizeError = (error: unknown, context: NormalizeContext = {}): NormalizedError => {
  const attached = getAttachedNormalized(error);
  if (attached) {
    // Classified once already, in the interceptor. Honour a caller's explicit tier, keep the rest.
    return context.tier && context.tier !== attached.tier ? { ...attached, tier: context.tier } : attached;
  }

  const { kind, status } = classify(error, context);
  const request = buildRequest(error);
  const responseData = axios.isAxiosError(error) ? error.response?.data : undefined;
  const serverMessage = kind === ErrorKind.EXPECTED ?
    truncate(messageOf(error), 300) :
    readServerMessage(responseData);

  const isExpected = kind === ErrorKind.EXPECTED ||
    kind === ErrorKind.AUTH_EXPIRED ||
    kind === ErrorKind.CHUNK_LOAD ||
    isNavigationFailure(error) ||
    (status != null && NON_REPORTABLE_STATUSES.includes(status));

  return {
    reference: newErrorReference(),
    kind,
    tier: context.tier ?? (isNavigationFailure(error) ? ErrorTier.SILENT : TIER_BY_KIND[kind]),
    // Derived rather than defaulted: an axios error is an http failure no matter which layer passed
    // it in, so a service that bypasses the interceptor is still tagged correctly.
    source: context.source ?? (axios.isAxiosError(error) ? ErrorSource.HTTP : ErrorSource.MANUAL),
    titleKey: COPY[kind].titleKey,
    messageKey: COPY[kind].messageKey,
    serverMessage,
    httpStatus: status,
    code: readErrorCode(responseData),
    request,
    isReportable: !isExpected,
    isRetryable: kind === ErrorKind.NETWORK || kind === ErrorKind.TIMEOUT || kind === ErrorKind.SERVER ||
      kind === ErrorKind.CHUNK_LOAD || kind === ErrorKind.UNKNOWN,
    fingerprint: fingerprintOf([kind, status, readErrorCode(responseData), request?.path], messageOf(error)),
    cause: error,
    occurredAt: new Date().toISOString()
  };
};
